import React, { useState, useRef } from 'react';
import {X, Calculator, RotateCcw, Droplets, Info, Download} from 'lucide-react';
import html2canvas from 'html2canvas';

interface CaudalTrapecialCalculatorProps {
  onClose: () => void;
}

interface Results {
  gasto: string;
  ahid: string;
  pmojado: string;
  rhid: string;
  eagua: string;
  velocidad: string;
  nfroud: string;
  energiaE: string;
  tflujo: string;
}

const initialResults: Results = {
  gasto: '',
  ahid: '',
  pmojado: '',
  rhid: '',
  eagua: '',
  velocidad: '',
  nfroud: '',
  energiaE: '',
  tflujo: '',
};

const CaudalTrapecialCalculator: React.FC<CaudalTrapecialCalculatorProps> = ({ onClose }) => {
  const [tirNormal, setTirNormal] = useState('');
  const [ancho, setAncho] = useState('');
  const [talud, setTalud] = useState('');
  const [rugosidad, setRugosidad] = useState('');
  const [pendiente, setPendiente] = useState('');
  const [results, setResults] = useState<Results>(initialResults);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  const truncate = (num: number) => Math.trunc(num * 10000.0) / 10000.0;

  const handleCalcular = () => {
    setError('');
    const yn = Number(tirNormal);
    const b = Number(ancho);
    const k = Number(talud);
    const n = Number(rugosidad);
    const s = Number(pendiente);

    if (!tirNormal || !ancho || talud === '' || !rugosidad || !pendiente || n === 0 || s < 0 || yn <= 0) {
      setError('Faltan datos o son incorrectos...HF');
      return;
    }

    const theta = Math.atan(s);
    const an = (b + k * yn) * yn;
    const pn = b + 2 * yn * Math.pow(Math.pow(k, 2) + 1, 0.5);
    const rh = an / pn;
    const q = (1 / n) * an * Math.pow(rh, 2 / 3) * Math.pow(s, 0.5);
    const te = b + 2 * k * yn;
    const vel = q / an;
    const fr = vel / Math.pow((9.81 * an) / te, 0.5);
    const ener = yn * Math.cos(theta) + Math.pow(vel, 2) / (2 * 9.81);

    setResults({
      gasto: truncate(q).toString(),
      ahid: truncate(an).toString(),
      pmojado: truncate(pn).toString(),
      rhid: truncate(rh).toString(),
      eagua: truncate(te).toString(),
      velocidad: truncate(vel).toString(),
      nfroud: truncate(fr).toString(),
      energiaE: truncate(ener).toString(),
      tflujo: fr > 1 ? 'Supercrítico' : 'Subcrítico',
    });
  };

  const handleLimpiar = () => {
    setTirNormal('');
    setAncho('');
    setTalud('');
    setRugosidad('');
    setPendiente('');
    setResults(initialResults);
    setError('');
  };

  const handleExportarPNG = async () => {
    if (!exportRef.current) return;
    if (!results.gasto) {
      setError('Primero debe calcular los resultados antes de exportar...HF');
      return;
    }
    setExporting(true);
    try {
      // Pequeño delay para que el badge se renderice
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      const date = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.download = `openHH-Caudal-Trapecial-${date}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error al exportar PNG:', err);
      setError('No se pudo generar el PNG. Intente nuevamente.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden relative">
        {/* Close button - always visible (sticky) */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-11 h-11 rounded-full bg-white text-gray-700 hover:bg-gray-100 shadow-lg flex items-center justify-center transition-all border border-gray-200"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 p-6 pr-20 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
              <Droplets className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Caudal Trapecial, Rectangular y Triangular</h2>
              <p className="text-blue-100 text-sm mt-1">Cálculo del gasto en canales abiertos · Manning</p>
            </div>
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-amber-50 border-b border-amber-100 px-6 py-3 flex items-start gap-2">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            Debe ingresar los datos para poder calcular el gasto que circula en canales <strong>trapezoidales, rectangulares o triangulares</strong>.
          </p>
        </div>

        {/* Body */}
        <div ref={exportRef} className="bg-white">
          {/* Watermark visible solo durante exportación */}
          {exporting && (
            <div className="px-6 pt-4 flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center">
                  <Droplets className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-800">openHH · HHTools</div>
                  <div className="text-xs text-gray-500">Caudal Trapecial, Rectangular y Triangular</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-400">Reporte generado</div>
                <div className="text-xs font-medium text-gray-600">{new Date().toLocaleString('es-ES')}</div>
              </div>
            </div>
          )}

          <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Datos a ingresar */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-5 border border-blue-100">
            <h3 className="text-center text-amber-700 font-bold mb-4 tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
              ─── Datos a ingresar ───
            </h3>

            {/* Diagrama dinámico del canal */}
            <ChannelDiagram
              tirante={Number(tirNormal) || 0}
              ancho={Number(ancho) || 0}
              talud={Number(talud) || 0}
            />
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  y = Tirante normal (m):
                </label>
                <input
                  type="number"
                  step="any"
                  value={tirNormal}
                  onChange={(e) => setTirNormal(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Ej. 1.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  B = Ancho de base o plantilla (m):
                </label>
                <input
                  type="number"
                  step="any"
                  value={ancho}
                  onChange={(e) => setAncho(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Ej. 2.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  K = Talud (Adimensional):
                </label>
                <input
                  type="number"
                  step="any"
                  value={talud}
                  onChange={(e) => setTalud(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Ej. 1.5 (0 para rectangular)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  n = Rugosidad (Adimensional):
                </label>
                <input
                  type="number"
                  step="any"
                  value={rugosidad}
                  onChange={(e) => setRugosidad(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Ej. 0.014"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  So = Pendiente (Adimensional):
                </label>
                <input
                  type="number"
                  step="any"
                  value={pendiente}
                  onChange={(e) => setPendiente(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Ej. 0.001"
                />
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium">
                ⚠ {error}
              </div>
            )}

            <div className="flex gap-3 mt-5">
              <button
                onClick={handleCalcular}
                className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-2.5 px-4 rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                <Calculator className="w-4 h-4" />
                Calcular
              </button>
              <button
                onClick={handleLimpiar}
                className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-4 rounded-lg transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                Limpiar
              </button>
            </div>
          </div>

          {/* Resultados */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-100">
            <h3 className="text-center text-amber-700 font-bold mb-4 tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
              ─── Resultados ───
            </h3>
            <div className="space-y-3">
              <ResultRow label="Q = Gasto del canal (m³/s)" value={results.gasto} highlight />
              <ResultRow label="Área Hidráulica (m²)" value={results.ahid} />
              <ResultRow label="Perímetro Mojado (m)" value={results.pmojado} />
              <ResultRow label="Radio Hidráulico (m)" value={results.rhid} />
              <ResultRow label="Espejo del Agua (m)" value={results.eagua} />
              <ResultRow label="Velocidad (m/s)" value={results.velocidad} />
              <ResultRow label="Núm. de Froude (Adim.)" value={results.nfroud} />
              <ResultRow label="Energía Específica (m)" value={results.energiaE} />
              <ResultRow label="Tipo de flujo" value={results.tflujo} flow />
            </div>
          </div>
          </div>

          {/* Marca "Generado por openHH" visible siempre dentro del export */}
          <div className="px-6 pb-4">
            <div className="flex items-center justify-between text-xs text-gray-500 border-t border-dashed border-gray-200 pt-3">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500"></div>
                <span className="font-semibold text-gray-600">Generado por openHH</span>
                <span className="text-gray-400">·</span>
                <span>HHTools</span>
              </div>
              <span className="text-gray-400">openCanalesWeb · Manning</span>
            </div>
          </div>
        </div>

        {/* Botón de exportación */}
        <div className="px-6 pb-5 pt-1 flex justify-end">
          <button
            onClick={handleExportarPNG}
            disabled={exporting}
            className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-5 rounded-lg shadow-md hover:shadow-lg transition-all"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Generando PNG...' : 'Exportar PNG'}
          </button>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 text-center text-xs text-gray-500 border-t border-gray-100">
          HHTools · openCanalesWeb — Cálculo basado en la ecuación de Manning
        </div>
      </div>
      </div>
    </div>
  );
};

const ResultRow: React.FC<{ label: string; value: string; highlight?: boolean; flow?: boolean }> = ({
  label,
  value,
  highlight,
  flow,
}) => {
  const flowColor = flow
    ? value === 'Supercrítico'
      ? 'text-rose-600 bg-rose-50 border-rose-200'
      : value === 'Subcrítico'
      ? 'text-blue-600 bg-blue-50 border-blue-200'
      : 'bg-white border-gray-200'
    : '';

  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`text-sm ${highlight ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
        {label}:
      </span>
      <input
        type="text"
        readOnly
        value={value}
        className={`w-32 px-3 py-1.5 text-sm rounded-lg border text-right font-mono ${
          highlight
            ? 'bg-amber-50 border-amber-300 text-amber-900 font-bold'
            : flow
            ? `${flowColor} font-semibold text-center`
            : 'bg-white border-gray-200 text-gray-800'
        }`}
      />
    </div>
  );
};

/**
 * Diagrama SVG dinámico de la sección transversal del canal.
 * Se adapta a sección triangular (B=0), rectangular (K=0) o trapecial.
 * Muestra los valores B, K e y ingresados por el usuario en tiempo real.
 */
const ChannelDiagram: React.FC<{ tirante: number; ancho: number; talud: number }> = ({
  tirante,
  ancho,
  talud,
}) => {
  // Dimensiones del SVG
  const W = 360;
  const H = 180;
  const padX = 50;
  const padTop = 30;
  const padBottom = 40;

  // Geometría real del canal
  const y = tirante > 0 ? tirante : 1;
  const b = ancho >= 0 ? ancho : 0;
  const k = talud >= 0 ? talud : 0;
  const topWidth = b + 2 * k * y; // ancho superficial real
  const safeTop = topWidth > 0 ? topWidth : 1;

  // Escala para encajar el ancho superior dentro del SVG
  const drawableW = W - padX * 2;
  const drawableH = H - padTop - padBottom;
  const scaleX = drawableW / safeTop;
  const scaleY = drawableH / y;
  const scale = Math.min(scaleX, scaleY) * 0.85;

  // Coordenadas (origen al centro inferior del trapecio)
  const cx = W / 2;
  const baseY = H - padBottom;
  const channelDepth = y * scale;
  const baseHalf = (b * scale) / 2;
  const topHalf = ((b + 2 * k * y) * scale) / 2;

  // Vértices del canal
  const blBase = { x: cx - baseHalf, y: baseY };
  const brBase = { x: cx + baseHalf, y: baseY };
  const tlTop = { x: cx - topHalf, y: baseY - channelDepth };
  const trTop = { x: cx + topHalf, y: baseY - channelDepth };

  // Tipo de sección
  const tipoSeccion =
    b === 0 && k === 0
      ? 'Sin sección'
      : b === 0
      ? 'Triangular'
      : k === 0
      ? 'Rectangular'
      : 'Trapecial';

  const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

  return (
    <div className="mb-4 bg-white rounded-lg border border-blue-200 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border-b border-blue-100">
        <span className="text-xs font-semibold text-blue-900">Sección transversal del canal</span>
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white text-blue-700 border border-blue-200">
          {tipoSeccion}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="waterGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0891b2" stopOpacity="0.95" />
          </linearGradient>
          <pattern id="groundPat" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#a16207" strokeWidth="1.2" opacity="0.4" />
          </pattern>
        </defs>

        {/* Terreno (relleno con patrón) */}
        <path
          d={`M0,${baseY} L0,${H} L${W},${H} L${W},${baseY} L${trTop.x},${tlTop.y} L${trTop.x},${baseY} L${brBase.x},${baseY} L${blBase.x},${baseY} L${tlTop.x},${baseY} L${tlTop.x},${tlTop.y} Z`}
          fill="url(#groundPat)"
        />
        <rect x="0" y={baseY} width={W} height={padBottom} fill="#fde68a" opacity="0.25" />

        {/* Línea de superficie del agua extendida */}
        <line
          x1="0"
          y1={tlTop.y}
          x2={tlTop.x}
          y2={tlTop.y}
          stroke="#0891b2"
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.5"
        />
        <line
          x1={trTop.x}
          y1={tlTop.y}
          x2={W}
          y2={tlTop.y}
          stroke="#0891b2"
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.5"
        />

        {/* Agua dentro del canal */}
        <polygon
          points={`${tlTop.x},${tlTop.y} ${trTop.x},${trTop.y} ${brBase.x},${brBase.y} ${blBase.x},${blBase.y}`}
          fill="url(#waterGrad)"
          stroke="#0e7490"
          strokeWidth="1.5"
        />

        {/* Onditas en la superficie */}
        <path
          d={`M${tlTop.x},${tlTop.y} Q${(tlTop.x + trTop.x) / 2 - 10},${tlTop.y - 3} ${(tlTop.x + trTop.x) / 2},${tlTop.y} T${trTop.x},${tlTop.y}`}
          fill="none"
          stroke="#ffffff"
          strokeWidth="1"
          opacity="0.7"
        />

        {/* Cota del tirante (y) - flecha vertical lateral */}
        <g>
          <line
            x1={trTop.x + 12}
            y1={tlTop.y}
            x2={trTop.x + 12}
            y2={baseY}
            stroke="#dc2626"
            strokeWidth="1.2"
          />
          <polygon
            points={`${trTop.x + 12},${tlTop.y} ${trTop.x + 8},${tlTop.y + 6} ${trTop.x + 16},${tlTop.y + 6}`}
            fill="#dc2626"
          />
          <polygon
            points={`${trTop.x + 12},${baseY} ${trTop.x + 8},${baseY - 6} ${trTop.x + 16},${baseY - 6}`}
            fill="#dc2626"
          />
          <text
            x={trTop.x + 18}
            y={(tlTop.y + baseY) / 2 + 4}
            fill="#b91c1c"
            fontSize="11"
            fontWeight="700"
          >
            y = {fmt(tirante)} m
          </text>
        </g>

        {/* Cota de la base (B) - flecha horizontal abajo */}
        <g>
          <line
            x1={blBase.x}
            y1={baseY + 18}
            x2={brBase.x}
            y2={baseY + 18}
            stroke="#1d4ed8"
            strokeWidth="1.2"
          />
          {b > 0 && (
            <>
              <polygon
                points={`${blBase.x},${baseY + 18} ${blBase.x + 6},${baseY + 14} ${blBase.x + 6},${baseY + 22}`}
                fill="#1d4ed8"
              />
              <polygon
                points={`${brBase.x},${baseY + 18} ${brBase.x - 6},${baseY + 14} ${brBase.x - 6},${baseY + 22}`}
                fill="#1d4ed8"
              />
            </>
          )}
          <text
            x={cx}
            y={baseY + 32}
            textAnchor="middle"
            fill="#1e40af"
            fontSize="11"
            fontWeight="700"
          >
            B = {fmt(ancho)} m
          </text>
        </g>

        {/* Indicador del talud K */}
        {k > 0 && b > 0 && (
          <g>
            <text
              x={(blBase.x + tlTop.x) / 2 - 24}
              y={(blBase.y + tlTop.y) / 2 + 4}
              fill="#9333ea"
              fontSize="10"
              fontWeight="700"
            >
              K = {fmt(talud)}
            </text>
            <text
              x={(blBase.x + tlTop.x) / 2 - 18}
              y={(blBase.y + tlTop.y) / 2 + 16}
              fill="#9333ea"
              fontSize="8"
            >
              (1V : {fmt(talud)}H)
            </text>
          </g>
        )}
        {k > 0 && b === 0 && (
          <text
            x={cx + 4}
            y={(baseY + tlTop.y) / 2}
            fill="#9333ea"
            fontSize="10"
            fontWeight="700"
          >
            K = {fmt(talud)}
          </text>
        )}

        {/* Etiqueta del espejo de agua (T) */}
        <text
          x={cx}
          y={tlTop.y - 8}
          textAnchor="middle"
          fill="#0e7490"
          fontSize="10"
          fontWeight="600"
        >
          T = {fmt(b + 2 * k * tirante)} m
        </text>
      </svg>
    </div>
  );
};

export default CaudalTrapecialCalculator;
